import { addMonths, differenceInMonths, endOfMonth, isAfter, isBefore } from 'date-fns';
import { coerceToDate, formatDate } from '@common/paymentsUtils/dateUtils';

interface NextDueDateInterface {
  firstDueDate: string;
  today?: Date;
  delay?: number;
  monthlyPaymentDate?: number;
}

interface RemainingTermLengthInterface {
  start: Date;
  end: string;
}

/**
 * Returns the next due date, formatted as a string.
 * @param firstDueDate string (Required)
 */
export const getNextDueDate = ({
  firstDueDate,
  today = new Date(),
  delay = 0,
  monthlyPaymentDate,
}: NextDueDateInterface): string => {
  const firstDueDateLocal = coerceToDate(firstDueDate)!;
  if (isBefore(today, firstDueDateLocal)) {
    const nextDueDateWithDelay = addMonths(firstDueDateLocal, delay);
    return formatDate(nextDueDateWithDelay, 'MMMM d, yyyy');
  }
  const monthsPast = differenceInMonths(today, firstDueDateLocal);
  const nextDueDate = addMonths(firstDueDateLocal, monthsPast + 1);
  const nextDueDateWithDelay = addMonths(nextDueDate, delay);
  if (monthlyPaymentDate) {
    const lastDayOfMonth = endOfMonth(nextDueDateWithDelay);
    const maxDayOfMonth = lastDayOfMonth.getDate();

    if (monthlyPaymentDate > maxDayOfMonth) {
      // Adjust monthlyPaymentDate to the maximum day of the month
      monthlyPaymentDate = maxDayOfMonth;
    }

    nextDueDateWithDelay.setDate(monthlyPaymentDate);
  }

  return formatDate(nextDueDateWithDelay, 'MMMM d, yyyy');
};

/**
 * Returns the remaining term length, in months.
 * @param endDateForPayments string (Required)
 */
export const getRemainingTermLengthInMonths = ({ start = new Date(), end }: RemainingTermLengthInterface): number => {
  const endDate = coerceToDate(end)!;
  if (isAfter(start, endDate)) {
    return 0;
  }
  let termLength = 0;
  for (let i = 1; i <= 12; i++) {
    const nextDate = addMonths(start, i);
    termLength++;
    if (isAfter(nextDate, endDate)) {
      break;
    }
  }
  return termLength;
};
